import React from 'react';
import { Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { convertToBlob } from '~/util';
import moment from 'moment';
import { ConfirmPatientModal } from '~/components/Patient';
import { PatientContext, PatientContextType } from '~/services';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { ReferralFileList } from './ReferralFileList';
import { ReferralFileUpload } from './ReferralFileUpload';

export interface ReferralAttachmentType {
    id: number;
    file: File;
}
interface ReferralAttachmentsProps {
    patient: PatientType;
    attachments: ReferralAttachmentType[];
    setAttachments: React.Dispatch<React.SetStateAction<ReferralAttachmentType[]>>;
    sendingReferral?: boolean;
}
export function ReferralAttachments({
    patient,
    attachments,
    setAttachments,
    sendingReferral,
}: ReferralAttachmentsProps) {
    const { externalReferralAttachment, clearExternalReferralAttachment } = React.useContext(
        ReferralContext
    ) as ReferralContextType;
    const [show, setShow] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [fileIncrement, setFileIncrement] = React.useState(0);
    const [showExternalConfirmation, setShowExternalConfirmation] = React.useState(false);
    const { hasConfirmedMatch, setConfirmedMatch } = React.useContext(
        PatientContext
    ) as PatientContextType;

    function handleExternalCancel() {
        clearExternalReferralAttachment();
        setShow(false);
        setShowExternalConfirmation(false);
    }

    function confirmMatch() {
        setConfirmedMatch(true);
        setShowExternalConfirmation(false);
    }

    React.useEffect(() => {
        if (!externalReferralAttachment) return;
        // Determine whether we need to show them the confirm patient message before the upload
        if (
            !hasConfirmedMatch &&
            (!externalReferralAttachment.autoSynced || !externalReferralAttachment.ehrOrigin)
        ) {
            setShowExternalConfirmation(true);
        }
        setShow(true);
    }, [clearExternalReferralAttachment, externalReferralAttachment, hasConfirmedMatch]);

    // add external referral attachment
    React.useEffect(() => {
        if (!externalReferralAttachment) return;
        // If the modal is already open, the external upload will reach here
        // before the confirmation modal gets swapped out, so wait for confirmation
        if (
            !hasConfirmedMatch &&
            (!externalReferralAttachment?.autoSynced || !externalReferralAttachment?.ehrOrigin)
        )
            return;
        const res = convertToBlob(externalReferralAttachment.document);
        const filename = `${
            externalReferralAttachment.ehrOrigin ? `${externalReferralAttachment.ehrOrigin} ` : ''
        }Document ${moment().format('YYYY-MM-DD-ss')}.pdf`;
        const file = new File([res], filename, { type: 'application/pdf' });
        const incrementId = fileIncrement + 1;
        setAttachments((prevAttachments) => [...prevAttachments, { id: incrementId, file }]);
        setFileIncrement(incrementId);
        clearExternalReferralAttachment();
    }, [
        externalReferralAttachment,
        clearExternalReferralAttachment,
        fileIncrement,
        hasConfirmedMatch,
        setAttachments,
    ]);

    return (
        <>
            <button
                disabled={sendingReferral}
                className="btn btn-dark me-md-3 mb-2 mb-md-0"
                type="button"
                onClick={() => setShow(true)}
            >
                <span className="badge bg-primary me-1 badge-font-90">
                    {attachments.length > 0 ? attachments.length : ''}
                </span>
                <i className="fa-solid fa-paperclip" /> Attachments
            </button>
            {showExternalConfirmation ? (
                <ConfirmPatientModal
                    patientName={`${patient.first_name} ${patient.last_name}`}
                    show={show}
                    setShow={setShow}
                    handleExternalCancel={handleExternalCancel}
                    confirmMatch={confirmMatch}
                />
            ) : (
                <BSModal
                    show={show}
                    onHide={() => setShow(false)}
                    className="attachments-modal"
                    aria-labelledby="attachments-title"
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="attachments-title" role="heading" aria-level={1}>
                            Attachments
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="permissions-alert">
                            To attach files directly from another website, use{' '}
                            <a
                                href="https://chrome.google.com/webstore/detail/zephyr-mic/ecfijpjflodeinpllfgnnglfpoailoab"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Member in Context
                            </a>
                            .
                        </div>
                        <ReferralFileUpload
                            attachments={attachments}
                            setAttachments={setAttachments}
                            editing={editing}
                            fileIncrement={fileIncrement}
                            setFileIncrement={setFileIncrement}
                        />
                        <ReferralFileList
                            attachments={attachments}
                            setAttachments={setAttachments}
                            setEditing={setEditing}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => setShow(false)}
                        >
                            Close
                        </button>
                    </Modal.Footer>
                </BSModal>
            )}
        </>
    );
}
